"use client";

import type { ProductPricingBlock } from "@mw/shared/payload-types";
import type { Locale } from "@mw/shared/i18n";
import type { ReactNode } from "react";

import { Tabs } from "react-aria-components";
import { useLocale, useTranslations } from "next-intl";
import type { Tier } from "@mw/shared/pricing";

import { TIERS } from "@mw/shared/pricing";
import { EventName } from "@mw/shared/analyticsEventNames";
import { useCurrency } from "@/app/_providers/currency";
import { publicUrls } from "@/constants";
import { formatLink } from "@utilities/payload";

import { CornerMarkerDot } from "../core/CornerMarkerDot";
import { DottedBorder } from "../core/GradientBorder";
import { LinkTracked } from "../core/LinkTracked";
import { TabList } from "../core/TabList";
import { PricingCardHeader } from "./PricingCardHeader";
import { PricingCardLayout } from "./PricingCardLayout";
import { getFormattedPrices, TIER_COVER_IMAGES } from "./utils";
import { usePricingTableContext } from "./PricingTableContext";

type CustomCard = NonNullable<ProductPricingBlock["customCards"]>[number];
type Feature = NonNullable<CustomCard["features"]>[number];

function FeatureItem({ feature, isLast, icon }: { feature: Feature; isLast: boolean; icon?: ReactNode }) {
  if (feature.highlighted) {
    return (
      <li>
        <div
          className='-tw-mx-5 tw-bg-white tw-mt-1 tw-mb-3 tw-py-4 tw-px-5'
          style={{ border: "1px solid rgba(0, 0, 0, 0.07)", borderRadius: 20 }}
        >
          <p className='tw-type-base tw-font-medium'>{feature.title}</p>
          {feature.description && <p className='tw-mt-3 tw-text-gray-600 tw-type-xs'>{feature.description}</p>}
        </div>
      </li>
    );
  }

  return (
    <li>
      <div className='tw-flex tw-items-center tw-gap-3 tw-py-3'>
        {icon}
        <span className='tw-type-xs'>{feature.title}</span>
      </div>
      {!isLast && <DottedBorder side='inline' />}
    </li>
  );
}

function CustomPricingCard({ card, iconElements }: { card: CustomCard; iconElements: Record<string, ReactNode> }) {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const locale = useLocale() as Locale;
  const { currency, currencySymbol } = useCurrency();
  const { billingCycle } = usePricingTableContext();

  const tier = TIERS[card.tier];
  if (!tier) return null;

  const coverImage = TIER_COVER_IMAGES[tier.planId];
  const period = billingCycle === "yearly" ? "yearly" : "monthly";

  const effectivePricing: Tier["pricing"] =
    card.monthlyPriceOverride != null || card.yearlyPriceOverride != null
      ? {
          ...tier.pricing,
          subscription: tier.pricing.subscription
            ? {
                monthly: { price: card.monthlyPriceOverride ?? tier.pricing.subscription.monthly.price },
                yearly: { price: card.yearlyPriceOverride ?? tier.pricing.subscription.yearly.price },
              }
            : tier.pricing.subscription,
        }
      : tier.pricing;

  const { price, discountedPrice, discountMessageKey, formattedOriginalPrice, currencyFormattedPrice } =
    getFormattedPrices(effectivePricing, period, currency, currencySymbol, locale);

  const isEnterprise = tier.planId === "enterprise";

  const displayPrice = isEnterprise ? t("custom_pricing") : currencyFormattedPrice;
  const displayPriceUnit = isEnterprise ? "" : t("period_month_long");

  const discountMessageContent =
    !isEnterprise && discountedPrice && price && formattedOriginalPrice && discountMessageKey ? (
      <p className='tw-opacity-80 tw-mb-2 tw-type-xs'>
        <span className='tw-line-through'>{formattedOriginalPrice}</span> <span>{t(discountMessageKey)}</span>
      </p>
    ) : undefined;

  const subtitle = card.subtitleOverride || t(`tiers.${card.tier}.description`);

  const ctaLink = card.ctaLink ? formatLink(card.ctaLink) : null;
  const ctaHref = ctaLink?.href ?? publicUrls.signUp;
  const ctaLabel = ctaLink?.label ?? t("cta_label");

  const features = card.features ?? [];

  return (
    <PricingCardLayout
      header={
        <PricingCardHeader
          title={card.planNameOverride || t(`tiers.${card.tier}.title`)}
          subtitle={<p className='tw-text-white tw-opacity-80 tw-type-xs'>{subtitle}</p>}
          price={displayPrice}
          priceUnit={displayPriceUnit}
          discountMessage={discountMessageContent}
          coverImage={coverImage}
          backgroundColor='dark'
          highlighted
          billingPeriodLabel={t(`billing_cycle.billed_${billingCycle === "yearly" ? "yearly" : "monthly"}`)}
        />
      }
      features={
        <ul className='tw-mt-6 tw-px-5'>
          {features.map((feature, index) => (
            <FeatureItem
              key={feature.id ?? index}
              feature={feature}
              isLast={index === features.length - 1}
              icon={feature.icon ? iconElements[feature.icon as string] : undefined}
            />
          ))}
        </ul>
      }
      footer={
        <div className='tw-mt-8 tw-mb-4'>
          <LinkTracked
            eventName={EventName.mw_pricing_card_cta_clicked}
            eventProperties={{ tier: tier.planId, variant: "custom-cards" }}
            asButton
            buttonVariants={{ variant: "elevated", size: "lg", expand: true }}
            href={ctaHref}
            label={ctaLabel}
          />
        </div>
      }
    />
  );
}

interface CustomPricingCardsProps {
  cards: ProductPricingBlock["customCards"];
  showAnnualToggle?: boolean | null;
  iconElements?: Record<string, ReactNode>;
}

export const CustomPricingCards = ({ cards, showAnnualToggle, iconElements = {} }: CustomPricingCardsProps) => {
  const t = useTranslations("PricingClientKeys.PricingCard");
  const { billingCycle, setBillingCycle } = usePricingTableContext();

  const billingTabs = [
    { id: "monthly", title: t("billing_cycle.monthly_short") },
    { id: "yearly", title: t("billing_cycle.yearly_short") },
  ];

  if (!cards?.length) return null;

  const cardsContent = (
    <div className='tw-overflow-x-auto tw-overflow-y-hidden tw-scrollbar-none tw-py-2 -tw-my-2'>
      <div className='tw-flex tw-flex-nowrap'>
        {cards.map((card, index) => {
          const isLast = index === cards.length - 1;
          return (
            <div key={card.id ?? index} className='tw-relative tw-min-w-[250px] tw-flex-1 tw-p-4'>
              <CustomPricingCard card={card} iconElements={iconElements} />
              {!isLast && (
                <div className='tw-absolute tw-right-0 tw-top-0 tw-bottom-0 tw-w-[1px] tw-bg-black/5'>
                  <CornerMarkerDot backgroundColor='white' position='topCentered' />
                  <CornerMarkerDot backgroundColor='white' position='bottomCentered' />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  if (!showAnnualToggle) {
    return <div>{cardsContent}</div>;
  }

  return (
    <Tabs selectedKey={billingCycle} onSelectionChange={key => setBillingCycle(key as "monthly" | "yearly")}>
      <div>
        <div
          className='tw-flex tw-justify-center tw-pt-8 tw-pb-12'
          style={{ borderBottom: "1px solid rgba(0, 0, 0, 0.07)" }}
        >
          <TabList items={billingTabs} showRing centered size='lg' />
        </div>
        {cardsContent}
      </div>
    </Tabs>
  );
};
